import { Alert, AlertContent, AlertText } from "@hortis/ui/alert";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@hortis/ui/breadcrumb";
import { Button, IconButton } from "@hortis/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHandle,
  DrawerRemoveScroll,
} from "@hortis/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { useIsScrolled } from "@hortis/ui/hooks";
import {
  AlertTriangle,
  AlignLeft1,
  DotsHorizontal,
  Edit2,
  Target1,
  Trash3,
} from "@hortis/ui/icons";
import { Transform } from "@hortis/ui/icons/transform";
import { TransformPlus } from "@hortis/ui/icons/transform-plus";
import { ScrollArea, ScrollAreaViewport } from "@hortis/ui/scroll-area";
import { Sheet } from "@hortis/ui/sheet";
import { Skeleton } from "@hortis/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { pipe } from "@mobily/ts-belt";
import { keepPreviousData } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { flatten } from "@turf/turf";
import {
  PlantMaterialStatus,
  SortOrder,
  useGetCollectionSiteFromNamesQuery,
} from "generated/graphql";
import { useState } from "react";
import { parseLocationGeometry } from "src/components/map/location-bed-polygons/parse-geometry";
import { useMaplibre } from "src/components/map/utils";
import {
  TopBar,
  TopBarActionButtons,
  TopBarContent,
} from "src/components/nav-layout/topbar";
import {
  RecordPageHeader,
  RecordPageHeaderButtons,
  RecordPageHeaderContent,
  RecordPageHeaderSubtitle,
  RecordPageHeaderTitle,
  RecordPageHeaderTitleGroup,
} from "src/components/record-page-header";
import { useMapContext } from "src/features/collection-map/map-store";
import { bbox, mapId } from "src/features/collection-map/utils";
import { DeleteLocationDialog } from "src/features/locations/components/delete-location-dialog";
import { EditLocationSheetContent } from "src/features/locations/components/location-drawer";
import {
  LocationMaterialsSection,
  MaterialSortColumn,
} from "src/features/locations/components/location-materials-section";
import { getLocationByNameDocument } from "src/features/locations/graphql";
import { useAccessRole } from "src/features/permissions/use-access-role";
import { RecordSectionContentGroup } from "src/features/records/components/record-section/new-record-section";
import { useControlledDebounce } from "src/utils/hooks/debounce";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { useMobile } from "src/utils/hooks/media-query";
import { truncateString } from "src/utils/truncate-string";
import { twMerge } from "tailwind-merge";
import { match } from "ts-pattern";

export const Route = createFileRoute(
  "/_layout/site/$siteSlug/map/location/$locationName",
)({
  component: Component,
});

export const ErrorAlert = () => (
  <Alert color="warning" className="my-4" data-cy="location-details-alert">
    <AlertTriangle className="shrink-0" />
    <AlertContent>
      <AlertText>
        Something went wrong whilst loading the details for this location.
      </AlertText>
    </AlertContent>
  </Alert>
);

// eslint-disable-next-line sonarjs/cognitive-complexity
function Component() {
  const {
    requestLocationEdit,
    collapseSidebar,
    sidebarOpen,
    refetchLocationGeometry,
  } = useMapContext();
  const { siteSlug, locationName } = Route.useParams();
  const { subdomain } = Route.useRouteContext();
  const [snap, setSnap] = useState<number | string | null>("224px");
  const { [mapId]: map } = useMaplibre();
  const isMobile = useMobile({ defaultMatches: true });
  const { attachRef } = useIsScrolled();
  const { canEdit } = useAccessRole();

  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);

  const [{ data: siteData, error: siteError, fetching: siteFetching }] =
    useGetCollectionSiteFromNamesQuery({
      variables: {
        organisationSubdomain: subdomain,
        collectionSiteSlug: siteSlug,
      },
    });

  const site = siteData?.org?.site;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const searchQueryDebouncer = useControlledDebounce((val: string) => {
    setDebouncedSearchQuery(val);
  });
  const [showAbsentMaterials, setShowAbsentMaterials] = useState(false);
  const [sortColumn, setSortColumn] =
    useState<MaterialSortColumn>("scientificName");
  const [sortOrder, setSortOrder] = useState(SortOrder.Ascending);

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    searchQueryDebouncer.schedule(val);
  };

  const {
    data,
    error,
    isFetching,
    refetch: refetchLocation,
  } = useGqlQuery({
    query: getLocationByNameDocument,
    variables: {
      id: site?.id ?? "",
      name: locationName,
      materialSort: match(sortColumn)
        .with("number", () => [
          { accessionNumber: { order: sortOrder } },
          { qualifier: { order: sortOrder } },
        ])
        .with("scientificName", () => [
          { scientificName: { order: sortOrder } },
        ])
        .exhaustive(),
      searchTerm: debouncedSearchQuery,
      materialFilter: showAbsentMaterials
        ? null
        : {
            status: {
              isNot: [PlantMaterialStatus.Absent],
            },
          },
    },
    enabled: site != null,
    placeholderData: keepPreviousData,
  });

  const location = data?.collectionSite?.location;
  const parsedLocationGeometry = parseLocationGeometry(location?.geometry);

  const navigate = Route.useNavigate();

  const recordHeaderButtons =
    location == null ? (
      <Skeleton className="h-6 w-48" />
    ) : (
      <RecordPageHeaderButtons>
        {canEdit && !location.archived && (
          <Tooltip>
            <TooltipTrigger asChild>
              <IconButton
                icon={
                  parsedLocationGeometry == null ? TransformPlus : Transform
                }
                onClick={() => {
                  requestLocationEdit(location);
                }}
                size="sm"
                variant="tertiaryGray"
                testId={
                  parsedLocationGeometry == null
                    ? "add-polygon-button"
                    : "update-polygon-button"
                }
                ariaLabel={
                  parsedLocationGeometry == null
                    ? "Add polygon"
                    : "Update polygon"
                }
              />
            </TooltipTrigger>
            <TooltipContent side="left">
              {parsedLocationGeometry == null
                ? "Add polygon"
                : "Update polygon"}
            </TooltipContent>
          </Tooltip>
        )}

        {(parsedLocationGeometry != null || !location.archived) && (
          <DropdownMenu>
            {!location.archived && (
              <DropdownMenuTrigger asChild>
                <IconButton
                  ariaLabel="More actions"
                  data-cy="action-menu-button"
                  icon={DotsHorizontal}
                  variant="tertiaryGray"
                  className="pointer-events-auto"
                  size="sm"
                />
              </DropdownMenuTrigger>
            )}
            <DropdownMenuContent align="end">
              {canEdit && !location.archived && (
                <>
                  <DropdownMenuItem
                    onClick={() => {
                      setEditDrawerOpen(true);
                    }}
                    data-cy="menu-option-edit"
                  >
                    <Edit2 className="h-4 w-4 text-grey-500" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuGroup>
                {parsedLocationGeometry != null && (
                  <>
                    <DropdownMenuItem
                      onClick={() => {
                        map?.fitBounds(
                          pipe(parsedLocationGeometry, flatten, bbox),
                          {
                            padding: 30,
                          },
                        );
                      }}
                      data-cy="menu-option-find-on-map"
                    >
                      <Target1 className="h-4 w-4 text-grey-500" />
                      Show on map
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuGroup>
              {parsedLocationGeometry != null && !location.archived && (
                <DropdownMenuSeparator />
              )}

              {canEdit && (
                <>
                  <DropdownMenuGroup>
                    {!location.archived && (
                      <DropdownMenuItem
                        onClick={() => {
                          setTrashDialogOpen(true);
                        }}
                        testId="archive-location-menu-item"
                      >
                        <Trash3 className="h-4 w-4 text-grey-500" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuGroup>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </RecordPageHeaderButtons>
    );

  const showLoading =
    (siteFetching && site == null) || (isFetching && location == null);
  const showError =
    (site == null && !siteFetching) ||
    (!isFetching && location == null) ||
    siteError != null ||
    error != null;

  const trashLocationAlert = location?.archived === true && (
    <Alert color="warning" className="mb-4" data-cy="trash-record-alert">
      <AlertContent className="flex flex-row gap-4">
        <Trash3 />
        <AlertText>This location has been deleted</AlertText>
      </AlertContent>
    </Alert>
  );

  const menuContent = (
    <>
      {showLoading && (
        <div>
          <div className="py-3 px-6 border-y border-grey-200 flex items-center justify-between gap-2">
            <Skeleton className="w-28 h-5" />
            <div className="flex items-center gap-1">
              <Skeleton className="size-9" />
              <Skeleton className="size-9" />
            </div>
          </div>
          <div className="space-y-6 px-6 py-6">
            {Array.from({ length: 8 }).map((_, index) => (
              <div className="flex items-center gap-3">
                <Skeleton className="size-12 rounded-lg" />
                <div className="space-y-2">
                  <Skeleton className="w-44 h-5" />
                  <Skeleton className="w-36 h-4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!showError && location != null && (
        <>
          <LocationMaterialsSection
            location={location}
            setShowAbsentMaterials={setShowAbsentMaterials}
            showAbsentMaterials={showAbsentMaterials}
            searchQuery={searchQuery}
            setSearchQuery={handleSearchChange}
            sortColumn={sortColumn}
            setSortColumn={setSortColumn}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            isFetching={isFetching}
          />
          <Sheet open={editDrawerOpen} onOpenChange={setEditDrawerOpen}>
            <EditLocationSheetContent
              onClose={() => {
                setEditDrawerOpen(false);
              }}
              open={editDrawerOpen}
              onLocationCreation={(updatedLocation) => {
                setEditDrawerOpen(false);
                if (updatedLocation.name !== location.name) {
                  void navigate({
                    to: "/site/$siteSlug/map/location/$locationName",
                    from: "/site/$siteSlug/map",
                    params: { locationName: updatedLocation.name },
                  });
                }
                refetchLocation();
                refetchLocationGeometry();
              }}
              initialValue={location}
            />
          </Sheet>

          {canEdit && trashDialogOpen && (
            <DeleteLocationDialog
              deleteLocationId={location.id}
              closeDeleteModal={() => {
                setTrashDialogOpen(false);
              }}
              refetchLocations={() => {
                refetchLocation();
                refetchLocationGeometry();
              }}
            />
          )}
        </>
      )}
    </>
  );

  return isMobile ? (
    <Drawer
      key={String(open)}
      open
      onOpenChange={(open) => {
        if (!open) {
          void navigate({
            from: "/site/$siteSlug/map/location/$locationName",
            to: "/site/$siteSlug/map",
          });
        }
      }}
      modal={false}
      shouldScaleBackground={false}
      snapPoints={["224px", 1]}
      activeSnapPoint={snap}
      setActiveSnapPoint={setSnap}
      scrollLockTimeout={50}
    >
      <DrawerContent
        className={twMerge(
          "h-full max-h-full shadow-md-up overflow-hidden",
          snap === 1 && "rounded-t-none",
        )}
        onInteractOutside={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <DrawerHandle />
        <DrawerRemoveScroll
          className={twMerge(
            "h-full pb-4",
            snap === 1 && "overflow-y-scroll",
            snap !== 1 && "overflow-clip",
          )}
        >
          <div ref={attachRef} className="flex w-full flex-col bg-white">
            <div className="flex flex-col gap-2 pb-4">
              {!showLoading && showError && <ErrorAlert />}
              {showLoading && (
                <div className="flex items-center justify-between gap-2 px-6 py-3">
                  <div className="space-y-2">
                    <Skeleton className="w-32 h-6" />
                    <Skeleton className="w-44 h-5" />
                  </div>
                  <div className="flex items-center gap-1">
                    <Skeleton className="size-9" />
                    <Skeleton className="size-9" />
                  </div>
                </div>
              )}

              {location != null && (
                <RecordPageHeader className="px-4 lg:px-6 justify-between">
                  <RecordPageHeaderTitleGroup>
                    {trashLocationAlert}
                    <RecordPageHeaderTitle>
                      <div className="flex w-full justify-between gap-2 items-center">
                        <span className="break-all">{location.name}</span>
                      </div>
                    </RecordPageHeaderTitle>

                    <RecordPageHeaderSubtitle>
                      {location.description ?? "-"}
                    </RecordPageHeaderSubtitle>
                  </RecordPageHeaderTitleGroup>
                  {recordHeaderButtons}
                </RecordPageHeader>
              )}
            </div>
            {menuContent}
          </div>
        </DrawerRemoveScroll>
      </DrawerContent>
    </Drawer>
  ) : (
    <div
      className={twMerge(
        "pointer-events-auto z-50 flex h-full w-full max-w-xs flex-col self-center bg-white shadow-md lg:max-w-md",
        !sidebarOpen && "hidden",
      )}
    >
      <TopBar>
        <TopBarContent className="gap-3">
          {showLoading && <Skeleton className="h-5 w-48" />}
          {!showError && location != null && (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <Button asChild variant="linkgray">
                    <Link
                      from="/site/$siteSlug/map/location/$locationName"
                      to="/site/$siteSlug/map/locations"
                    >
                      Map
                    </Link>
                  </Button>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {truncateString(location.name, 10)}
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          )}
        </TopBarContent>
        {!showError && (
          <TopBarActionButtons>
            <IconButton
              size="sm"
              variant="tertiaryGray"
              onClick={() => {
                collapseSidebar();
              }}
              icon={AlignLeft1}
              ariaLabel="Collapse location details"
              testId="location-details-collapse-button"
            />
          </TopBarActionButtons>
        )}
      </TopBar>
      <ScrollArea>
        <ScrollAreaViewport
          ref={attachRef}
          className="flex flex-col gap-4 bg-white max-w-full"
        >
          {showLoading && (
            <div className="flex items-center justify-between gap-2 px-6 py-4.5">
              <div className="space-y-2">
                <Skeleton className="w-40 h-6" />
                <Skeleton className="w-60 h-5" />
              </div>
              <div className="flex items-center gap-1">
                <Skeleton className="size-9" />
                <Skeleton className="size-9" />
              </div>
            </div>
          )}

          {!showLoading && showError && <ErrorAlert />}

          {!showError && location != null && (
            <>
              <RecordPageHeader className="px-6 py-4">
                <RecordPageHeaderContent>
                  <RecordPageHeaderTitleGroup>
                    {trashLocationAlert}
                    <RecordPageHeaderTitle>
                      <div className="flex w-full justify-between gap-2">
                        <p className="flex w-full items-center gap-2 break-all">
                          {location.name}
                        </p>
                      </div>
                    </RecordPageHeaderTitle>

                    <RecordPageHeaderSubtitle>
                      {location.description ?? "-"}
                    </RecordPageHeaderSubtitle>
                  </RecordPageHeaderTitleGroup>
                </RecordPageHeaderContent>
                {recordHeaderButtons}
              </RecordPageHeader>
            </>
          )}
          {menuContent}
        </ScrollAreaViewport>
      </ScrollArea>
    </div>
  );
}
